import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import styled from 'styled-components';
import { FiAlertCircle, FiCrop, FiLayers, FiMap, FiSearch } from 'react-icons/fi';
import DrawingViewer from './drawing-ui/DrawingViewer.jsx';
import { DRAW_TOOLBAR_ITEMS } from './drawing-ui/DrawToolbar.jsx';
import ReviewLanding from './drawing-ui/ReviewLanding.jsx';
import CollapsibleSideShell from './drawing-ui/CollapsibleSideShell.jsx';
import LeftSidebarTabs from './drawing-ui/LeftSidebarTabs.jsx';
import ReviewRightPanel from './drawing-ui/ReviewRightPanel.jsx';
import { buildAuthHeaders } from '../utils/openPdfReference.js';
import { fetchDrawingIr, fetchGeometrySheets, fetchFolderDrawingStats, fetchPdfOnlyDrawingSheets, fetchUserMarkupsApi, persistUserMarkupsApi, ruleLabel } from '../services/drawingGeometryApi.js';
import { REVIEW_PDF_ONLY_FALLBACK } from '../utils/userAccess.js';
import { deriveSheetScale } from '../utils/drawingMeasurements.js';
import { loadScaleOverride, resolveEffectiveSheetScale, scaleOverrideKey } from '../utils/sheetScaleStore.js';
import { componentHasEditableVertices, translateComponentPdfVertices } from '../utils/markupGeometry.js';
import {
  componentSupportsOffset,
  mirrorComponent,
  offsetComponent,
  parseOffsetDistanceInput,
  rotateComponent,
} from '../utils/markupTransform.js';
import { fetchReviewFolders } from '../services/reviewFoldersApi.js';
import { getIssueDrawingReviewTarget } from './compliance-ui/complianceIssuePresenters.js';
import {
  findComponentForCrossRefTarget,
} from '../utils/reviewObject.js';
import {
  DRAW_TOOLS,
  isUserDrawnComponent,
  loadUserMarkups,
  markupStorageKey,
  newUserMarkupId,
  normalizeUserMarkup,
  saveUserMarkups,
} from '../utils/userMarkup.js';
import {
  createMarkupHistory,
  pushMarkupSnapshot,
  redoMarkupSnapshot,
  undoMarkupSnapshot,
} from '../utils/markupUndo.js';
import {
  VISIBILITY_LENSES,
  shouldShowLabelOverlays,
  shouldShowVectorMarkups,
} from '../utils/visibilityLenses.js';
const Page = styled.div`
  display: flex;
  gap: 0;
  padding: 0;
  margin: 0;
  height: calc(100vh - var(--topnav-height, 110px));
  min-height: 0;
  overflow: hidden;
`;

const ViewerCanvas = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  background: #525659;
`;

const ViewerContent = styled.div`
  flex: 1;
  min-height: 0;
  position: relative;
`;

const PreviewBanner = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  z-index: 20;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(233, 112, 36, 0.3);
  border-radius: 10px;
  font-size: 12px;
  color: #5c3d1e;
  line-height: 1.45;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  pointer-events: none;
`;

function mergeIrWithOverrides(ir, overrides) {
  if (!ir || !overrides || !Object.keys(overrides).length) return ir;
  return {
    ...ir,
    components: (ir.components || []).map((c) => (
      overrides[c.id] ? { ...c, ...overrides[c.id] } : c
    )),
  };
}

function folderFromNavState(reviewNavState) {
  if (!reviewNavState?.requirementId) return null;
  return {
    requirementId: reviewNavState.requirementId,
    linkId: reviewNavState.linkId || '',
    title: reviewNavState.folderTitle || 'Linked folder',
    jobId: reviewNavState.jobId || '',
  };
}

function folderFromIssueTarget(target, fallbackTitle) {
  if (!target?.requirementId) return null;
  return {
    requirementId: target.requirementId,
    linkId: target.linkId || '',
    title: fallbackTitle || target.docName || 'Linked folder',
    jobId: '',
  };
}

function buildUserMarkupCopy(base, geometryComponent) {
  return {
    ...geometryComponent,
    id: newUserMarkupId(),
    label: base.label || base.kind || 'User markup (copy)',
    kind: base.kind || '',
    taxonomyAxis: base.taxonomyAxis,
    mappingStatus: base.kind
      ? (base.mappingStatus || 'user_confirmed')
      : 'pending_user_confirm',
    style: base.style ? { ...base.style } : undefined,
    createdAt: new Date().toISOString(),
  };
}

export default function ReviewTable({
  selectedProject,
  reviewNavState,
  onNavigateToLinkFolders,
  onNavigateToCompliance,
}) {
  const isIssueNav = reviewNavState?.mode === 'issue';
  const [activeFolder, setActiveFolder] = useState(() => folderFromNavState(reviewNavState));
  const [availableFolders, setAvailableFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [pdfUrl, setPdfUrl] = useState('');
  const [loadedSheets, setLoadedSheets] = useState([]);
  const [activeIr, setActiveIr] = useState(null);
  const [activeIrKey, setActiveIrKey] = useState(null);
  const [activeEntityPages, setActiveEntityPages] = useState([]);
  const [highlightComponentId, setHighlightComponentId] = useState(null);
  const [mappingPreview, setMappingPreview] = useState(null);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [selectedIssueId, setSelectedIssueId] = useState(reviewNavState?.issueId || null);
  const [viewerPage, setViewerPage] = useState(reviewNavState?.pageNumber || 1);
  const [rightPanelTab, setRightPanelTab] = useState(isIssueNav ? 'issues' : 'mapping');
  // Owned here only because the panel that produces it and the viewer that
  // draws it are siblings. `SheetFrameProposalsPanel` owns the selection
  // itself and clears it on every sheet/page change; this mirrors it.
  const [sheetFrameSelection, setSheetFrameSelection] = useState(null);
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [sheetsCollapsed, setSheetsCollapsed] = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(!isIssueNav);
  const [folderStats, setFolderStats] = useState({});
  const [sharedPdfDoc, setSharedPdfDoc] = useState(null);
  const [activeHatId, setActiveHatId] = useState('architect');
  const [issueHighlightText, setIssueHighlightText] = useState(
    reviewNavState?.highlightText || '',
  );
  const [visibilityLens, setVisibilityLens] = useState(VISIBILITY_LENSES.CLEAN);
  const [overlayFilters, setOverlayFilters] = useState({
    status: 'all',
    axis: 'all',
    kind: 'all',
  });
  const [componentOverrides, setComponentOverrides] = useState({});
  const [viewportInfo, setViewportInfo] = useState(null);
  const [activeDrawTool, setActiveDrawTool] = useState(DRAW_TOOLS.SELECT);
  const [userMarkups, setUserMarkups] = useState([]);
  const [selectedUserMarkupId, setSelectedUserMarkupId] = useState(null);
  const [floatingInspectorDismissed, setFloatingInspectorDismissed] = useState(false);
  const [intentPromptMarkupId, setIntentPromptMarkupId] = useState(null);
  const [modifyMode, setModifyMode] = useState(null);
  const [scaleOverride, setScaleOverride] = useState(null);
  const markupPersistTimerRef = useRef(null);
  const markupHistoryRef = useRef(createMarkupHistory());
  const userMarkupsRef = useRef([]);
  const pendingIssueNavRef = useRef(reviewNavState);
  const initialSheetSelectedRef = useRef(false);
  const activeIrKeyRef = useRef(null);
  const activeSourcePdfKeyRef = useRef(null);
  const pdfUrlCacheRef = useRef(new Map());
  const labelsActiveRef = useRef(false);
  const availableFoldersRef = useRef([]);
  const activeFolderRef = useRef(null);

  const projectId = selectedProject?.id;
  const effectiveRequirementId = activeFolder?.requirementId || reviewNavState?.requirementId;
  const firmId = selectedProject?.companyId || projectId;

  useEffect(() => { activeIrKeyRef.current = activeIrKey; }, [activeIrKey]);
  useEffect(() => { availableFoldersRef.current = availableFolders; }, [availableFolders]);
  useEffect(() => { activeFolderRef.current = activeFolder; }, [activeFolder]);

  useEffect(() => {
    if (!activeFolder?.requirementId || activeFolder.drawingFiles?.length) return;
    const match = availableFolders.find((f) => f.requirementId === activeFolder.requirementId);
    if (match?.drawingFiles?.length) {
      setActiveFolder((prev) => ({
        ...prev,
        drawingFiles: match.drawingFiles,
        drawingCount: match.drawingCount,
        linkId: prev.linkId || match.linkId,
        jobId: prev.jobId || match.jobId,
      }));
    }
  }, [availableFolders, activeFolder?.requirementId, activeFolder?.drawingFiles?.length]);

  useEffect(() => {
    labelsActiveRef.current = (
      visibilityLens === VISIBILITY_LENSES.HAT
      || visibilityLens === VISIBILITY_LENSES.SELECTION
      || Boolean(mappingPreview?.componentId)
      || isIssueNav
    );
  }, [visibilityLens, mappingPreview?.componentId, isIssueNav]);

  useEffect(() => {
    const fromNav = folderFromNavState(reviewNavState);
    if (fromNav) setActiveFolder(fromNav);
    if (reviewNavState?.issueId) setSelectedIssueId(reviewNavState.issueId);
    if (reviewNavState?.pageNumber) setViewerPage(reviewNavState.pageNumber);
    if (reviewNavState?.highlightText) setIssueHighlightText(reviewNavState.highlightText);
    if (reviewNavState?.mode === 'issue') {
      setRightCollapsed(false);
      setRightPanelTab('issues');
    }
    pendingIssueNavRef.current = reviewNavState;
  }, [
    reviewNavState?.mode,
    reviewNavState?.requirementId,
    reviewNavState?.linkId,
    reviewNavState?.folderTitle,
    reviewNavState?.jobId,
    reviewNavState?.issueId,
    reviewNavState?.pageNumber,
    reviewNavState?.originalPdfKey,
    reviewNavState?.componentId,
    reviewNavState?.highlightText,
  ]);

  useEffect(() => {
    if (!projectId) {
      setAvailableFolders([]);
      setFoldersLoading(false);
      return;
    }
    let cancelled = false;
    setFoldersLoading(true);
    fetchReviewFolders(projectId)
      .then((folders) => {
        if (!cancelled) setAvailableFolders(folders);
      })
      .catch((err) => {
        console.error('[ReviewTable] fetch folders error:', err);
        if (!cancelled) setAvailableFolders([]);
      })
      .finally(() => {
        if (!cancelled) setFoldersLoading(false);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  const resolvePdfUrl = useCallback(async (sourceKey) => {
    if (!sourceKey || !projectId) return null;
    const api = process.env.REACT_APP_PDF_SIGNED_URL_API;
    if (!api) return null;
    try {
      const res = await fetch(api, {
        method: 'POST',
        headers: buildAuthHeaders(),
        body: JSON.stringify({ projectId, originalPdfKey: sourceKey }),
      });
      const data = await res.json();
      return data.url || data.presignedPdfUrl || data.pdfUrl || null;
    } catch (err) {
      console.error('[ReviewTable] PDF signed URL failed:', err);
      return null;
    }
  }, [projectId]);

  const selectSheet = useCallback(async (sheet, opts = {}) => {
    if (!sheet) return;
    if (opts.prefetchOnly) {
      const key = sheet.sourcePdfKey || sheet.ir?.storage?.sourceKey;
      if (key && !pdfUrlCacheRef.current.has(key)) {
        const url = sheet.sourcePdfUrl || await resolvePdfUrl(key);
        if (url) pdfUrlCacheRef.current.set(key, url);
      }
      return;
    }

    const switchingSheet = sheet.irKey !== activeIrKey;
    const newSourceKey = sheet.sourcePdfKey || sheet.ir?.storage?.sourceKey || null;

    setActiveIrKey(sheet.irKey || null);

    if (switchingSheet && newSourceKey !== activeSourcePdfKeyRef.current) {
      setSharedPdfDoc(null);
      activeSourcePdfKeyRef.current = newSourceKey;
    }

    if (opts.page) setViewerPage(opts.page);
    else if (!opts.preserveHighlight) setViewerPage(1);

    if (!opts.preserveHighlight) {
      setHighlightComponentId(null);
      setMappingPreview(null);
      setSelectedComponent(null);
    }

    // PDF-first: show drawing immediately
    if (sheet.sourcePdfUrl) {
      setPdfUrl(sheet.sourcePdfUrl);
      if (newSourceKey) pdfUrlCacheRef.current.set(newSourceKey, sheet.sourcePdfUrl);
    } else if (newSourceKey) {
      const cached = pdfUrlCacheRef.current.get(newSourceKey);
      if (cached) setPdfUrl(cached);
      resolvePdfUrl(newSourceKey).then((url) => {
        if (!url || activeIrKeyRef.current !== sheet.irKey) return;
        pdfUrlCacheRef.current.set(newSourceKey, url);
        setPdfUrl(url);
      });
    }

    setActiveEntityPages(sheet.entityPages || []);
    if (sheet.pdfOnly || !sheet.ir) {
      setActiveIr(null);
    } else {
      setActiveIr(mergeIrWithOverrides(sheet.ir, componentOverrides));
    }

    const summaryUrl = sheet.irSummaryUrl || sheet.ir?.storage?.summaryUrl;
    const needsRefetch = !sheet.pdfOnly
      && summaryUrl
      && !sheet.ir?.components?.length
      && (sheet.componentCount > 0 || sheet.ir?.mappingCheck != null);

    if (needsRefetch) {
      const targetKey = sheet.irKey;
      fetchDrawingIr(summaryUrl)
        .then((ir) => {
          if (activeIrKeyRef.current !== targetKey) return;
          const merged = mergeIrWithOverrides(ir, componentOverrides);
          setActiveIr(merged);
          setLoadedSheets((prev) => prev.map((s) => (
            s.irKey === targetKey ? { ...s, ir: merged } : s
          )));
        })
        .catch((err) => {
          console.warn('[ReviewTable] IR refetch failed:', err);
        });
    }
  }, [resolvePdfUrl, activeIrKey, componentOverrides]);

  const handleSheetsLoaded = useCallback((sheets) => {
    setLoadedSheets(sheets);
  }, []);

  const loadedSheetsRef = useRef([]);
  useEffect(() => { loadedSheetsRef.current = loadedSheets; }, [loadedSheets]);

  const mapSheetMeta = useCallback((s, ir = null) => ({
    irKey: s.irKey,
    sheetId: s.sheetId,
    ir: ir ?? s.ir,
    irSummaryUrl: s.irSummaryUrl,
    entityPages: s.entityPages || [],
    sourcePdfKey: s.sourcePdfKey,
    sourcePdfUrl: s.sourcePdfUrl,
    unknownsRemaining: s.unknownsRemaining,
    componentCount: s.componentCount,
    extractionStatus: s.extractionStatus || s.ir?.extractionStatus || ir?.extractionStatus,
    pagesExtracted: s.pagesExtracted ?? s.ir?.pagesExtracted ?? ir?.pagesExtracted,
    pageCount: s.pageCount ?? s.ir?.pageCount ?? ir?.pageCount,
    pdfOnly: Boolean(s.pdfOnly),
  }), []);

  const loadSheetsForBrowse = useCallback(async ({ quiet = false } = {}) => {
    if (!projectId || !effectiveRequirementId) return;
    if (!quiet) setSheetsLoading(true);
    try {
      const prevSheets = loadedSheetsRef.current;
      const data = await fetchGeometrySheets(
        projectId,
        effectiveRequirementId,
        { includeIr: !quiet },
      );

      let sheetList = data.sheets || [];
      if (!sheetList.length && REVIEW_PDF_ONLY_FALLBACK) {
        const folder = availableFoldersRef.current.find(
          (f) => f.requirementId === effectiveRequirementId,
        ) || activeFolderRef.current;
        const pdfFallback = await fetchPdfOnlyDrawingSheets(
          projectId,
          effectiveRequirementId,
          folder?.drawingFiles,
        );
        sheetList = pdfFallback.sheets || [];
        if (sheetList.length && process.env.NODE_ENV === 'development') {
          console.info(
            '[ReviewTable] PDF-only mode —',
            sheetList.length,
            'drawing(s); no IR / vector pipeline required',
          );
        }
      }

      let loaded = sheetList.map((s) => {
        if (!quiet) return mapSheetMeta(s);
        const prev = prevSheets.find((p) => p.irKey === s.irKey);
        return mapSheetMeta(s, prev?.ir);
      });

      if (quiet && sheetList.length && sheetList.every((s) => s.pdfOnly)) {
        setLoadedSheets(loaded);
        return;
      }

      if (quiet && activeIrKey) {
        const meta = (data.sheets || []).find((s) => s.irKey === activeIrKey);
        const prev = prevSheets.find((s) => s.irKey === activeIrKey);
        const progressChanged = meta && prev && (
          meta.pagesExtracted !== prev.pagesExtracted
          || meta.componentCount !== prev.componentCount
          || (meta.extractionStatus || meta.ir?.extractionStatus)
            !== prev.extractionStatus
        );
        if (progressChanged && meta?.irSummaryUrl && labelsActiveRef.current) {
          try {
            const ir = await fetchDrawingIr(meta.irSummaryUrl);
            loaded = loaded.map((s) => (
              s.irKey === activeIrKey
                ? mapSheetMeta(meta, mergeIrWithOverrides(ir, componentOverrides))
                : s
            ));
          } catch (err) {
            console.warn('[ReviewTable] quiet IR refresh failed:', err);
          }
        }
      }

      setLoadedSheets(loaded);

      const activeKey = activeIrKey;
      const stillActive = activeKey && loaded.find((s) => s.irKey === activeKey);
      if (stillActive && labelsActiveRef.current) {
        const prev = prevSheets.find((s) => s.irKey === activeKey);
        const irChanged = stillActive.ir && (
          !prev?.ir
          || stillActive.ir.pagesExtracted !== prev.ir.pagesExtracted
          || stillActive.ir.extractionStatus !== prev.ir.extractionStatus
          || (stillActive.ir.components?.length ?? 0) !== (prev.ir.components?.length ?? 0)
        );
        if (!quiet || irChanged) {
          setActiveIr(mergeIrWithOverrides(stillActive.ir, componentOverrides) || null);
          setActiveEntityPages(stillActive.entityPages || []);
        }
      } else if (loaded[0] && !initialSheetSelectedRef.current) {
        await selectSheet(loaded[0]);
        initialSheetSelectedRef.current = true;
      }
    } catch (err) {
      console.error('[ReviewTable] load sheets error:', err);
      if (!quiet) {
        try {
          if (REVIEW_PDF_ONLY_FALLBACK) {
            const folder = availableFoldersRef.current.find(
              (f) => f.requirementId === effectiveRequirementId,
            ) || activeFolderRef.current;
            const pdfFallback = await fetchPdfOnlyDrawingSheets(
              projectId,
              effectiveRequirementId,
              folder?.drawingFiles,
            );
            const fallbackSheets = (pdfFallback.sheets || []).map((s) => mapSheetMeta(s));
            if (fallbackSheets.length) {
              setLoadedSheets(fallbackSheets);
              if (!initialSheetSelectedRef.current) {
                await selectSheet(fallbackSheets[0]);
                initialSheetSelectedRef.current = true;
              }
              return;
            }
          }
        } catch (fallbackErr) {
          console.warn('[ReviewTable] pdf-only fallback failed:', fallbackErr);
        }
        setLoadedSheets([]);
        setPdfUrl('');
        setActiveIr(null);
        setActiveIrKey(null);
        setActiveEntityPages([]);
      }
    } finally {
      if (!quiet) setSheetsLoading(false);
    }
  }, [projectId, effectiveRequirementId, activeIrKey, selectSheet, mapSheetMeta, componentOverrides]);

  const hasExtractingSheets = useMemo(
    () => loadedSheets.some((s) => s.extractionStatus === 'extracting'),
    [loadedSheets]
  );

  useEffect(() => {
    if (!effectiveRequirementId || !hasExtractingSheets) return undefined;
    const timer = setInterval(() => {
      loadSheetsForBrowse({ quiet: true });
    }, 8000);
    return () => clearInterval(timer);
  }, [effectiveRequirementId, hasExtractingSheets, loadSheetsForBrowse]);

  useEffect(() => {
    initialSheetSelectedRef.current = false;
    if (effectiveRequirementId) loadSheetsForBrowse();
  }, [effectiveRequirementId, loadSheetsForBrowse]);

  useEffect(() => {
    if (!REVIEW_PDF_ONLY_FALLBACK) return;
    if (!effectiveRequirementId || sheetsLoading || loadedSheets.length > 0) return;
    const folder = availableFolders.find((f) => f.requirementId === effectiveRequirementId);
    if (!folder?.drawingFiles?.length) return;
    loadSheetsForBrowse();
  }, [availableFolders, effectiveRequirementId, loadedSheets.length, sheetsLoading, loadSheetsForBrowse]);

  const matchSheetForTarget = useCallback((sheets, target) => {
    if (!target) return null;
    return sheets.find((s) => (
      s.sourcePdfKey === target.originalPdfKey
      || s.sheetId === target.sheetId
      || (target.sheetId && String(s.irKey || '').includes(target.sheetId))
    )) || null;
  }, []);

  const applyDrawingTarget = useCallback(async (target, { componentId, pageNumber } = {}) => {
    if (!target?.originalPdfKey) return;

    if (!activeFolder || activeFolder.requirementId !== target.requirementId) {
      const folder = availableFolders.find((f) => f.requirementId === target.requirementId)
        || folderFromIssueTarget(target);
      if (folder) setActiveFolder(folder);
    }

    setViewerPage(pageNumber || target.pageNumber || 1);
    setHighlightComponentId(componentId || target.componentId || null);

    const url = await resolvePdfUrl(target.originalPdfKey);
    if (url) setPdfUrl(url);

    const sheet = matchSheetForTarget(loadedSheets, target);
    if (sheet) {
      setActiveIr(sheet.ir || null);
      setActiveIrKey(sheet.irKey || null);
      setActiveEntityPages(sheet.entityPages || []);
    } else {
      setActiveIr(null);
      setActiveIrKey(null);
    }
  }, [activeFolder, availableFolders, loadedSheets, matchSheetForTarget, resolvePdfUrl]);

  const handleSelectIssue = useCallback(async (issue) => {
    const target = getIssueDrawingReviewTarget(issue);
    if (!target) return;
    setSelectedIssueId(issue.id);
    setIssueHighlightText(
      target.statement || issue.evidenceStatement || issue.identifiedIssue || '',
    );
    setRightPanelTab('issues');
    setRightCollapsed(false);
    await applyDrawingTarget(target, {
      componentId: target.componentId,
      pageNumber: target.pageNumber,
    });
    if (target.componentId) {
      const sheet = matchSheetForTarget(loadedSheets, target);
      const comp = sheet?.ir?.components?.find((c) => c.id === target.componentId);
      if (comp) {
        setSelectedComponent(comp);
        setRightPanelTab('detail');
      }
    }
  }, [applyDrawingTarget, loadedSheets, matchSheetForTarget]);

  useEffect(() => {
    const nav = pendingIssueNavRef.current;
    if (!nav?.originalPdfKey || !isIssueNav) return;
    applyDrawingTarget(
      {
        requirementId: nav.requirementId,
        linkId: nav.linkId,
        originalPdfKey: nav.originalPdfKey,
        sheetId: nav.sheetId,
        pageNumber: nav.pageNumber,
        componentId: nav.componentId,
      },
      {
        componentId: nav.componentId,
        pageNumber: nav.pageNumber,
      }
    );
  }, [isIssueNav, loadedSheets, applyDrawingTarget]);

  useEffect(() => {
    if (!selectedIssueId || !loadedSheets.length) return;
    const nav = pendingIssueNavRef.current;
    if (!nav?.originalPdfKey) return;
    const sheet = matchSheetForTarget(loadedSheets, nav);
    if (sheet?.ir) {
      setActiveIr(sheet.ir);
      setActiveIrKey(sheet.irKey || null);
    }
  }, [loadedSheets, selectedIssueId, matchSheetForTarget]);

  const handleSelectMappingComponent = useCallback(async (bucket) => {
    const item = bucket?.exemplar;
    if (!item) return;
    const sheet = loadedSheets.find((s) => s.irKey === item.irKey) || bucket.sheet;
    const page = (item.pageIndex ?? 0) + 1;

    setMappingPreview({
      componentId: item.id,
      pageNumber: page,
      ruleKey: bucket.ruleKey,
      matchedRule: bucket.matchedRule,
      instanceCount: bucket.instanceCount,
      label: item.label || item.value || item.id,
      sheetId: item.sheetId,
    });
    setViewerPage(page);
    setHighlightComponentId(item.id);
    setSelectedComponent(item);
    setRightPanelTab('mapping');
    setRightCollapsed(false);

    if (!sheet) return;
    if (sheet.irKey === activeIrKey) {
      if (sheet.ir) setActiveIr(sheet.ir);
      if (sheet.entityPages?.length) setActiveEntityPages(sheet.entityPages);
      return;
    }
    await selectSheet(sheet, { preserveHighlight: true, page });
  }, [loadedSheets, activeIrKey, selectSheet]);

  const handleComponentClick = useCallback((comp) => {
    const merged = componentOverrides[comp.id]
      ? { ...comp, ...componentOverrides[comp.id] }
      : comp;
    setHighlightComponentId(merged.id);
    setSelectedComponent(merged);
    setSelectedUserMarkupId(isUserDrawnComponent(merged) ? merged.id : null);
    if (isUserDrawnComponent(merged)) {
      setFloatingInspectorDismissed(false);
      setActiveDrawTool(DRAW_TOOLS.SELECT);
    }
    if (visibilityLens === VISIBILITY_LENSES.CLEAN) {
      setVisibilityLens(VISIBILITY_LENSES.SELECTION);
    }
  }, [componentOverrides, visibilityLens]);

  const handleLocalOverride = useCallback((componentId, patches) => {
    setComponentOverrides((prev) => ({
      ...prev,
      [componentId]: { ...(prev[componentId] || {}), ...patches },
    }));
    setSelectedComponent((prev) => (
      prev?.id === componentId ? { ...prev, ...patches } : prev
    ));
    setUserMarkups((prev) => prev.map((m) => (
      m.id === componentId ? { ...m, ...patches } : m
    )));
    setActiveIr((prev) => {
      if (!prev?.components) return prev;
      return {
        ...prev,
        components: prev.components.map((c) => (
          c.id === componentId ? { ...c, ...patches } : c
        )),
      };
    });
  }, []);

  const handleInspectorSaved = useCallback((result) => {
    if (result?.componentId && result?.component) {
      setComponentOverrides((prev) => {
        const next = { ...prev };
        delete next[result.componentId];
        return next;
      });
      setSelectedComponent(result.component);
      setActiveIr((prev) => {
        if (!prev?.components) return prev;
        return {
          ...prev,
          components: prev.components.map((c) => (
            c.id === result.componentId ? { ...c, ...result.component } : c
          )),
        };
      });
    }
    loadSheetsForBrowse({ quiet: true });
  }, [loadSheetsForBrowse]);

  const handleCrossRefNavigate = useCallback(async ({ targetSheet, targetDetail, sourceComponentId }) => {
    const target = loadedSheets.find(
      (s) => s.sheetId === targetSheet
        || s.ir?.sheetId === targetSheet
        || s.irKey?.includes(String(targetSheet)),
    );
    if (!target) return;

    const page = 1;
    await selectSheet(target, { preserveHighlight: false, page });

    const ir = target.ir || mergeIrWithOverrides(activeIr, componentOverrides);
    const match = findComponentForCrossRefTarget(ir, { targetDetail, targetSheet });
    if (match) {
      const merged = componentOverrides[match.id]
        ? { ...match, ...componentOverrides[match.id] }
        : match;
      setHighlightComponentId(merged.id);
      setSelectedComponent(merged);
      setViewerPage((match.pageIndex ?? 0) + 1);
      setVisibilityLens(VISIBILITY_LENSES.SELECTION);
    } else {
      setHighlightComponentId(null);
      setSelectedComponent(null);
    }
    setRightPanelTab('detail');
    setRightCollapsed(false);
  }, [loadedSheets, selectSheet, activeIr, componentOverrides]);

  const handleViewerPageChange = useCallback((page) => {
    setViewerPage(page);
  }, []);

  const handleSelectThumbnailPage = useCallback((page) => {
    setViewerPage(page);
  }, []);

  const rightRailActions = useMemo(() => ([
    {
      id: 'detail',
      label: 'Object inspector',
      icon: <FiSearch size={20} />,
      onClick: () => {
        setRightPanelTab('detail');
        setRightCollapsed(false);
      },
    },
    {
      id: 'issues',
      label: 'Compliance issues',
      icon: <FiAlertCircle size={20} />,
      onClick: () => {
        setRightPanelTab('issues');
        setRightCollapsed(false);
      },
    },
    {
      id: 'mapping',
      label: 'Mapping check',
      icon: <FiMap size={20} />,
      onClick: () => {
        setRightPanelTab('mapping');
        setRightCollapsed(false);
      },
    },
    {
      id: 'sheetFrame',
      label: 'Sheet frame proposals',
      icon: <FiCrop size={20} />,
      onClick: () => {
        setRightPanelTab('sheetFrame');
        setRightCollapsed(false);
      },
    },
  ]), []);

  const activeSheetMeta = useMemo(
    () => loadedSheets.find((s) => s.irKey === activeIrKey) || null,
    [loadedSheets, activeIrKey]
  );

  const activeSheetId = activeSheetMeta?.sheetId || activeIr?.sheetId || '';

  const selectedUserMarkup = useMemo(() => {
    if (!selectedUserMarkupId) return null;
    const markup = userMarkups.find((m) => m.id === selectedUserMarkupId);
    if (!markup) return null;
    return componentOverrides[markup.id]
      ? { ...markup, ...componentOverrides[markup.id] }
      : markup;
  }, [userMarkups, selectedUserMarkupId, componentOverrides]);

  const selectedExtractedComponent = useMemo(() => {
    if (!selectedComponent || isUserDrawnComponent(selectedComponent)) return null;
    const merged = componentOverrides[selectedComponent.id]
      ? { ...selectedComponent, ...componentOverrides[selectedComponent.id] }
      : selectedComponent;
    return componentHasEditableVertices(merged) ? merged : null;
  }, [selectedComponent, componentOverrides]);

  const viewerPageIndex = Math.max(0, (viewerPage || 1) - 1);

  const detectedSheetScale = useMemo(() => {
    const comps = activeIr?.components || [];
    if (!comps.length) return null;
    return deriveSheetScale(comps, activeEntityPages, viewerPageIndex)
      || deriveSheetScale(comps, activeEntityPages, null)
      || deriveSheetScale(comps, [], null);
  }, [activeIr?.components, activeEntityPages, viewerPageIndex]);

  const effectiveSheetScale = useMemo(
    () => resolveEffectiveSheetScale(detectedSheetScale, scaleOverride),
    [detectedSheetScale, scaleOverride],
  );

  const effectiveScaleRatio = effectiveSheetScale?.ratio ?? null;

  useEffect(() => {
    if (selectedUserMarkupId) setFloatingInspectorDismissed(false);
  }, [selectedUserMarkupId]);

  useEffect(() => {
    if (!projectId || !effectiveRequirementId || !activeSheetId) {
      setUserMarkups([]);
      setSelectedUserMarkupId(null);
      setScaleOverride(null);
      return;
    }
    const key = markupStorageKey(projectId, effectiveRequirementId, activeSheetId);
    const scaleKey = scaleOverrideKey(projectId, effectiveRequirementId, activeSheetId);
    setScaleOverride(loadScaleOverride(scaleKey));
    setSelectedUserMarkupId(null);
    markupHistoryRef.current = createMarkupHistory();

    const applyLoadedMarkups = (raw) => {
      if (cancelled) return;
      setUserMarkups((raw || []).map(normalizeUserMarkup));
    };

    let cancelled = false;
    fetchUserMarkupsApi(projectId, effectiveRequirementId, activeSheetId)
      .then(({ markups }) => {
        if (markups?.length) {
          applyLoadedMarkups(markups);
        } else {
          applyLoadedMarkups(loadUserMarkups(key));
        }
      })
      .catch(() => {
        applyLoadedMarkups(loadUserMarkups(key));
      });
    return () => { cancelled = true; };
  }, [projectId, effectiveRequirementId, activeSheetId]);

  useEffect(() => {
    if (!projectId || !effectiveRequirementId || !activeSheetId) return;
    const key = markupStorageKey(projectId, effectiveRequirementId, activeSheetId);
    saveUserMarkups(key, userMarkups);
    if (markupPersistTimerRef.current) clearTimeout(markupPersistTimerRef.current);
    markupPersistTimerRef.current = setTimeout(() => {
      persistUserMarkupsApi(projectId, effectiveRequirementId, activeSheetId, userMarkups);
    }, 600);
    return () => {
      if (markupPersistTimerRef.current) clearTimeout(markupPersistTimerRef.current);
    };
  }, [userMarkups, projectId, effectiveRequirementId, activeSheetId]);

  useEffect(() => {
    userMarkupsRef.current = userMarkups;
  }, [userMarkups]);

  const checkpointMarkupHistory = useCallback(() => {
    pushMarkupSnapshot(markupHistoryRef.current, userMarkupsRef.current);
  }, []);

  const handleMarkupUndo = useCallback(() => {
    setUserMarkups((current) => undoMarkupSnapshot(markupHistoryRef.current, current) || current);
  }, []);

  const handleMarkupRedo = useCallback(() => {
    setUserMarkups((current) => redoMarkupSnapshot(markupHistoryRef.current, current) || current);
  }, []);

  const handleUserMarkupComplete = useCallback((component) => {
    checkpointMarkupHistory();
    setUserMarkups((prev) => [...prev, component]);
    setSelectedUserMarkupId(component.id);
    setSelectedComponent(component);
    setHighlightComponentId(component.id);
    setActiveDrawTool(DRAW_TOOLS.SELECT);
    setFloatingInspectorDismissed(false);
    setIntentPromptMarkupId(component.kind ? null : component.id);
    setVisibilityLens(VISIBILITY_LENSES.SELECTION);
  }, [checkpointMarkupHistory]);

  const handleUserMarkupSelect = useCallback((component) => {
    if (!component) {
      setSelectedUserMarkupId(null);
      setHighlightComponentId(null);
      return;
    }
    setSelectedUserMarkupId(component.id);
    setSelectedComponent(component);
    setHighlightComponentId(component.id);
    setFloatingInspectorDismissed(false);
    if (component.kind) {
      setIntentPromptMarkupId((prev) => (prev === component.id ? null : prev));
    }
  }, []);

  const handleUserMarkupUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    setUserMarkups((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
    setSelectedComponent((prev) => (prev?.id === updated.id ? updated : prev));
  }, []);

  const handleCopyUserMarkup = useCallback(() => {
    if (!selectedUserMarkup) return;
    checkpointMarkupHistory();
    const offset = 24;
    const copy = translateComponentPdfVertices(selectedUserMarkup, offset, offset, {
      scaleRatio: effectiveScaleRatio,
    });
    const withMeta = buildUserMarkupCopy(selectedUserMarkup, copy);
    setUserMarkups((prev) => [...prev, withMeta]);
    setSelectedUserMarkupId(withMeta.id);
    setSelectedComponent(withMeta);
    setHighlightComponentId(withMeta.id);
    setFloatingInspectorDismissed(false);
  }, [selectedUserMarkup, effectiveScaleRatio, checkpointMarkupHistory]);

  const handleRotateUserMarkup = useCallback((degrees = 90, { pivot = null } = {}) => {
    if (!selectedUserMarkup) return;
    checkpointMarkupHistory();
    const updated = rotateComponent(selectedUserMarkup, degrees, {
      scaleRatio: effectiveScaleRatio,
      pivot,
    });
    handleUserMarkupUpdate(updated);
  }, [selectedUserMarkup, effectiveScaleRatio, checkpointMarkupHistory, handleUserMarkupUpdate]);

  const handleUserMarkupSave = useCallback((markupId, patches) => {
    handleLocalOverride(markupId, patches);
    if (patches?.kind) {
      setIntentPromptMarkupId((prev) => (prev === markupId ? null : prev));
    }
  }, [handleLocalOverride]);

  const handleMirrorUserMarkup = useCallback((axisA, axisB, { copy = false } = {}) => {
    if (!selectedUserMarkup || !axisA || !axisB) return;
    checkpointMarkupHistory();
    const mirrored = mirrorComponent(selectedUserMarkup, axisA, axisB, {
      scaleRatio: effectiveScaleRatio,
      splitPolyline: true,
    });
    if (copy) {
      const withMeta = buildUserMarkupCopy(selectedUserMarkup, mirrored);
      setUserMarkups((prev) => [...prev, withMeta]);
      setSelectedUserMarkupId(withMeta.id);
      setSelectedComponent(withMeta);
      setHighlightComponentId(withMeta.id);
      setFloatingInspectorDismissed(false);
      return;
    }
    handleUserMarkupUpdate(mirrored);
  }, [selectedUserMarkup, effectiveScaleRatio, checkpointMarkupHistory, handleUserMarkupUpdate]);

  const handleOffsetUserMarkup = useCallback((signedDistance, { copy = false } = {}) => {
    if (!selectedUserMarkup) return;
    const distancePdf = parseOffsetDistanceInput(
      Math.abs(signedDistance),
      effectiveScaleRatio,
    );
    if (!distancePdf) return;
    checkpointMarkupHistory();
    const updated = offsetComponent(selectedUserMarkup, distancePdf * Math.sign(signedDistance), {
      scaleRatio: effectiveScaleRatio,
    });
    if (copy) {
      const withMeta = buildUserMarkupCopy(selectedUserMarkup, updated);
      setUserMarkups((prev) => [...prev, withMeta]);
      setSelectedUserMarkupId(withMeta.id);
      setSelectedComponent(withMeta);
      setHighlightComponentId(withMeta.id);
      setFloatingInspectorDismissed(false);
      return;
    }
    handleUserMarkupUpdate(updated);
  }, [selectedUserMarkup, effectiveScaleRatio, checkpointMarkupHistory, handleUserMarkupUpdate]);

  const handleExtractedGeometryUpdate = useCallback((updated) => {
    if (!updated?.id) return;
    handleLocalOverride(updated.id, {
      geometry: updated.geometry,
      bbox: updated.bbox,
      measurements: updated.measurements,
    });
  }, [handleLocalOverride]);

  const handleDrawToolChange = useCallback((tool) => {
    setActiveDrawTool(tool);
    if (tool !== DRAW_TOOLS.SELECT) {
      setFloatingInspectorDismissed(true);
      setModifyMode(null);
    }
  }, []);

  const handleDeleteSelectedMarkup = useCallback((markupId) => {
    const id = markupId || selectedUserMarkupId;
    if (!id) return;
    checkpointMarkupHistory();
    setUserMarkups((prev) => prev.filter((m) => m.id !== id));
    setComponentOverrides((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (selectedUserMarkupId === id) {
      setSelectedUserMarkupId(null);
      setSelectedComponent(null);
      setHighlightComponentId(null);
    }
  }, [selectedUserMarkupId, checkpointMarkupHistory]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA') return;

      if (!e.metaKey && !e.ctrlKey && !e.altKey) {
        const key = e.key.length === 1 ? e.key.toLowerCase() : '';
        const toolMatch = DRAW_TOOLBAR_ITEMS.find((t) => t.hotkey?.toLowerCase() === key);
        if (toolMatch) {
          e.preventDefault();
          handleDrawToolChange(toolMatch.id);
          return;
        }
      }

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleMarkupUndo();
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'y'
        || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleMarkupRedo();
        return;
      }

      if (!selectedUserMarkupId || activeDrawTool !== DRAW_TOOLS.SELECT) return;
      if (e.key === 'Delete') {
        e.preventDefault();
        handleDeleteSelectedMarkup();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    selectedUserMarkupId,
    activeDrawTool,
    handleDeleteSelectedMarkup,
    handleDrawToolChange,
    handleMarkupUndo,
    handleMarkupRedo,
  ]);

  const handleSelectFolder = useCallback((folder) => {
    initialSheetSelectedRef.current = false;
    setActiveFolder(folder);
    setLoadedSheets([]);
    setPdfUrl('');
    setSharedPdfDoc(null);
    setActiveIr(null);
    setActiveIrKey(null);
    setHighlightComponentId(null);
    setMappingPreview(null);
    setSelectedComponent(null);
    setVisibilityLens(VISIBILITY_LENSES.CLEAN);
    setComponentOverrides({});
    setUserMarkups([]);
    setSelectedUserMarkupId(null);
    setActiveDrawTool(DRAW_TOOLS.SELECT);
    setRightPanelTab('mapping');
    setRightCollapsed(true);
  }, []);

  const handleFolderSelectChange = (e) => {
    const id = e.target.value;
    const folder = availableFolders.find((f) => f.requirementId === id);
    if (folder) handleSelectFolder(folder);
  };

  const drawingFolders = useMemo(
    () => availableFolders.filter((f) => f.drawingCount > 0),
    [availableFolders]
  );

  // Fetch aggregate label stats for drawing folders shown on the landing page
  useEffect(() => {
    if (!projectId || drawingFolders.length === 0) return;
    let cancelled = false;
    Promise.all(
      drawingFolders.map(async (folder) => {
        const stats = await fetchFolderDrawingStats(projectId, folder.requirementId);
        return { id: folder.requirementId, stats };
      })
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ id, stats }) => { map[id] = stats; });
      setFolderStats(map);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [projectId, drawingFolders]);

  const viewerEmptyMessage = useMemo(() => {
    if (isIssueNav && selectedIssueId) return 'Loading drawing for selected issue…';
    if (isIssueNav) return 'Select a compliance issue in the panel to load drawing evidence.';
    if (!activeFolder) return null;
    if (sheetsLoading) return 'Loading drawing sheets…';
    if (loadedSheets.length > 0) {
      if (loadedSheets.every((s) => s.pdfOnly)) {
        return 'PDF preview — vector extraction is off. Use draw tools to mark up; labels appear when extraction runs.';
      }
      if (hasExtractingSheets) {
        return 'Sheet available — extraction in progress. Completed pages are visible; mapping updates as pages finish.';
      }
      return 'Select a sheet on the left, then pick a page thumbnail to navigate.';
    }
    const folderDrawings = availableFolders.find(
      (f) => f.requirementId === effectiveRequirementId,
    )?.drawingCount;
    if (folderDrawings > 0) {
      return 'Loading PDF preview…';
    }
    return 'No drawing PDFs found in this linked folder. Confirm PDFs as Drawing deliverables on Link Folders.';
  }, [activeFolder, sheetsLoading, loadedSheets, isIssueNav, selectedIssueId, hasExtractingSheets, availableFolders, effectiveRequirementId]);

  const showLanding = !activeFolder && !isIssueNav && !reviewNavState?.requirementId;
  const workspaceFolder = activeFolder;
  const issueRequirementId = workspaceFolder?.requirementId || reviewNavState?.requirementId;
  const isComplianceIssuesView = rightPanelTab === 'issues' || isIssueNav;

  const viewerIr = useMemo(
    () => mergeIrWithOverrides(activeIr, componentOverrides),
    [activeIr, componentOverrides],
  );

  const showLabelOverlays = shouldShowLabelOverlays({
    visibilityLens,
    issueHighlightOnly: isComplianceIssuesView,
    previewComponentId: rightPanelTab === 'mapping' ? mappingPreview?.componentId : null,
    highlightComponentId,
    issueHighlightText,
  });

  const showVectorLayer = shouldShowVectorMarkups({ visibilityLens });

  const effectiveLens = isComplianceIssuesView && (highlightComponentId || issueHighlightText)
    ? VISIBILITY_LENSES.ISSUE
    : visibilityLens;

  if (showLanding) {
    const foldersWithStats = availableFolders.map((f) => ({
      ...f,
      stats: folderStats[f.requirementId] || null,
    }));
    return (
      <ReviewLanding
        folders={foldersWithStats}
        loading={foldersLoading}
        onSelectFolder={handleSelectFolder}
        onGoToLinkFolders={onNavigateToLinkFolders}
      />
    );
  }

  return (
    <Page>
      <CollapsibleSideShell
        side="left"
        collapsed={sheetsCollapsed}
        onToggle={() => setSheetsCollapsed(true)}
        onExpand={() => setSheetsCollapsed(false)}
        expandedWidth="clamp(280px, 30vw, 420px)"
        railIcon={<FiLayers size={20} />}
        collapseLabel="Collapse sheet explorer"
      >
        <LeftSidebarTabs
          sheets={loadedSheets}
          sheetsLoading={sheetsLoading}
          activeIrKey={activeIrKey}
          activeSheet={activeSheetMeta}
          activePage={viewerPage}
          pdfUrl={pdfUrl}
          pdfDoc={sharedPdfDoc}
          drawingFolders={drawingFolders}
          activeRequirementId={issueRequirementId}
          onFolderChange={handleFolderSelectChange}
          onSelectSheet={selectSheet}
          onSelectPage={handleSelectThumbnailPage}
          onNavigateToLinkFolders={onNavigateToLinkFolders}
          hasIndexingInProgress={hasExtractingSheets}
          entityPages={activeEntityPages}
          viewportInfo={viewportInfo}
          projectId={projectId}
          requirementId={issueRequirementId}
          scaleOverride={scaleOverride}
          onScaleOverrideChange={setScaleOverride}
        />
      </CollapsibleSideShell>

      <ViewerCanvas>
        <ViewerContent>
          <DrawingViewer
            pdfUrl={pdfUrl}
            pdfDocExternal={sharedPdfDoc}
            onPdfDocReady={setSharedPdfDoc}
            ir={viewerIr}
            entityPages={activeEntityPages}
            highlightComponentId={
              (isComplianceIssuesView || visibilityLens === VISIBILITY_LENSES.SELECTION)
                ? highlightComponentId
                : null
            }
            previewComponentId={rightPanelTab === 'mapping' ? mappingPreview?.componentId : null}
            previewPage={rightPanelTab === 'mapping' ? mappingPreview?.pageNumber : null}
            onComponentClick={showLabelOverlays ? handleComponentClick : undefined}
            onPageChange={handleViewerPageChange}
            showOverlays={Boolean(pdfUrl)}
            showIrLabels={showLabelOverlays}
            showVectorMarkups={showVectorLayer}
            visibilityLens={effectiveLens}
            onVisibilityLensChange={setVisibilityLens}
            activeHatId={activeHatId}
            onHatChange={setActiveHatId}
            overlayFilters={overlayFilters}
            onOverlayFiltersChange={setOverlayFilters}
            issueHighlightOnly={isComplianceIssuesView}
            issueHighlightText={issueHighlightText}
            emptyMessage={viewerEmptyMessage}
            initialPage={viewerPage}
            onViewportInfoChange={setViewportInfo}
            drawTool={activeDrawTool}
            onDrawToolChange={handleDrawToolChange}
            userMarkups={userMarkups}
            selectedUserMarkupId={selectedUserMarkupId}
            selectedUserMarkup={
              selectedUserMarkup
              && (!floatingInspectorDismissed || selectedUserMarkup.id === intentPromptMarkupId)
                ? selectedUserMarkup
                : null
            }
            onUserMarkupComplete={handleUserMarkupComplete}
            onUserMarkupSelect={handleUserMarkupSelect}
            onUserMarkupUpdate={handleUserMarkupUpdate}
            onUserMarkupHistoryCheckpoint={checkpointMarkupHistory}
            onUserMarkupSave={handleUserMarkupSave}
            onUserMarkupDelete={handleDeleteSelectedMarkup}
            onDismissFloatingInspector={() => {
              if (selectedUserMarkup?.id === intentPromptMarkupId) return;
              setFloatingInspectorDismissed(true);
              setIntentPromptMarkupId(null);
            }}
            onOpenFullInspector={() => {
              setRightPanelTab('detail');
              setRightCollapsed(false);
            }}
            modifyMode={modifyMode}
            onModifyModeChange={setModifyMode}
            onCopyUserMarkup={handleCopyUserMarkup}
            onRotateUserMarkup={handleRotateUserMarkup}
            onMirrorUserMarkup={handleMirrorUserMarkup}
            onOffsetUserMarkup={handleOffsetUserMarkup}
            intentPromptMarkupId={intentPromptMarkupId}
            scaleRatio={effectiveScaleRatio}
            selectedExtractedComponent={selectedExtractedComponent}
            onExtractedGeometryUpdate={handleExtractedGeometryUpdate}
            userDrawSheetId={activeSheetId}
            sheetFrameSelectedProposal={
              // Gated on the active tab, matching previewComponentId above:
              // leaving the tab puts the band overlay away with it.
              rightPanelTab === 'sheetFrame' ? (sheetFrameSelection?.proposal || null) : null
            }
            sheetFrameProposals={
              rightPanelTab === 'sheetFrame' ? (sheetFrameSelection?.proposals || []) : []
            }
            sheetFramePageEvidence={
              rightPanelTab === 'sheetFrame' ? (sheetFrameSelection?.pageEvidence || null) : null
            }
          />

          {rightPanelTab === 'mapping' && mappingPreview && !rightCollapsed && (
            <PreviewBanner>
              <strong>Previewing exemplar</strong>
              {' — '}
              {mappingPreview.label}
              {' · '}
              {ruleLabel(mappingPreview.matchedRule)}
              {' · '}
              Sheet {mappingPreview.sheetId}
              {' · '}
              <strong>{mappingPreview.instanceCount}</strong>
              {' instance'}
              {mappingPreview.instanceCount === 1 ? '' : 's'}
              {' on publish'}
            </PreviewBanner>
          )}
        </ViewerContent>
      </ViewerCanvas>

      <CollapsibleSideShell
        side="right"
        collapsed={rightCollapsed}
        onToggle={() => setRightCollapsed(true)}
        onExpand={() => setRightCollapsed(false)}
        expandedWidth="clamp(300px, 32vw, 420px)"
        railActions={rightRailActions}
        activeRailId={rightPanelTab}
        persistentRail
        collapseLabel="Collapse review panel"
      >
        <ReviewRightPanel
          tab={rightPanelTab}
          onTabChange={setRightPanelTab}
          selectedComponent={selectedComponent}
          activeSheetId={activeSheetMeta?.sheetId || activeIr?.sheetId}
          projectId={projectId}
          requirementId={issueRequirementId}
          firmId={firmId}
          hatId={activeHatId}
          onHatChange={setActiveHatId}
          selectedIssueId={selectedIssueId}
          onSelectIssue={handleSelectIssue}
          onNavigateToCompliance={onNavigateToCompliance}
          onSelectMappingComponent={handleSelectMappingComponent}
          onSheetsLoaded={handleSheetsLoaded}
          onOpenMappingFromDetail={() => setRightPanelTab('mapping')}
          onInspectorSaved={handleInspectorSaved}
          onLocalOverride={handleLocalOverride}
          onCrossRefNavigate={handleCrossRefNavigate}
          sheetMeta={activeSheetMeta}
          sheetIr={viewerIr}
          entityPages={activeEntityPages}
          scaleOverride={scaleOverride}
          sheetFramePageIndex={viewerPageIndex}
          onSheetFrameSelectionChange={setSheetFrameSelection}
        />
      </CollapsibleSideShell>
    </Page>
  );
}
